import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  OnInit,
  OnDestroy,
  inject,
  signal,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { EditorModule } from 'primeng/editor';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';

export interface HtmlDescriptionConfig {
  placeholder?: string;
  height?: string;
  minHeight?: string;
  maxHeight?: string;
  showToolbar?: boolean;
  readonly?: boolean;
  disabled?: boolean;
  required?: boolean;
  maxLength?: number;
}

@Component({
  selector: 'app-html-description',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    EditorModule,
    TranslateModule,
  ],
  templateUrl: './html-description.component.html',
  styleUrl: './html-description.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HtmlDescriptionComponent),
      multi: true,
    },
  ],
})
export class HtmlDescriptionComponent
  implements ControlValueAccessor, OnInit, OnDestroy
{
  private readonly translateService = inject(TranslateService);
  private readonly destroy$ = new Subject<void>();

  @Input() config: HtmlDescriptionConfig = {};
  @Input() placeholder =
    'Enter product asset description with rich text formatting';
  @Input() height = '200px';
  @Input() minHeight = '120px';
  @Input() maxHeight = '400px';
  @Input() showToolbar = true;
  @Input() readonly = false;
  @Input() disabled = false;
  @Input() required = false;
  @Input() maxLength = 5000;

  @Output() contentChange = new EventEmitter<string>();
  @Output() editorFocus = new EventEmitter<void>();
  @Output() editorBlur = new EventEmitter<void>();

  // ControlValueAccessor properties
  private onChange = (value: string) => {};
  private onTouched = () => {};

  // Component state
  content = signal<string>('');
  isFocused = signal<boolean>(false);
  isDisabled = signal<boolean>(false);
  isReadonly = signal<boolean>(false);

  // Computed properties
  computedConfig = computed(() => ({
    placeholder: this.config.placeholder || this.placeholder,
    height: this.config.height || this.height,
    minHeight: this.config.minHeight || this.minHeight,
    maxHeight: this.config.maxHeight || this.maxHeight,
    showToolbar:
      this.config.showToolbar !== false && this.showToolbar !== false,
    readonly: this.config.readonly || this.readonly,
    disabled: this.config.disabled || this.disabled,
    required: this.config.required || this.required,
    maxLength: this.config.maxLength || this.maxLength,
  }));

  // Editor modules configuration for Quill - simple configuration
  editorModules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ font: [] }],
      [{ size: ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ script: 'sub' }, { script: 'super' }],
      ['blockquote', 'code-block'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ direction: 'rtl' }],
      [{ align: [] }],
      ['link', 'image'],
      ['clean'],
    ],
  };

  ngOnInit(): void {
    this.updateState();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this.content.set(value || '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
    this.updateState();
  }

  // Component methods
  onContentChange(event: any): void {
    const newContent = event.htmlValue || event.textValue || '';
    this.content.set(newContent);
    this.onChange(newContent);
    this.contentChange.emit(newContent);
  }

  onSelectionChange(event: any): void {
    // Handle selection changes if needed
    // This can be used for toolbar state management
  }

  // Get current content for p-editor
  getCurrentContent(): string {
    return this.content();
  }

  private updateState(): void {
    const config = this.computedConfig();
    this.isDisabled.set(config.disabled);
    this.isReadonly.set(config.readonly);
  }

  // Utility methods
  getCharacterCount(): number {
    const textContent = this.content().replaceAll(/<[^>]*>/g, '');
    return textContent.length;
  }

  isOverLimit(): boolean {
    const count = this.getCharacterCount();
    const limit = this.computedConfig().maxLength;
    return count > limit;
  }

  getRemainingCharacters(): number {
    return Math.max(
      0,
      this.computedConfig().maxLength - this.getCharacterCount(),
    );
  }
}
